import { Task, TaskStats, Settings, SyncStatus } from "./tauri";
import { providerLabel } from "./ai-pwa";

export type DelegationTarget = "taiga" | "codex" | "claude" | "grok" | "hermes";
export type CommandAction = "A" | "B" | "C" | "D";
export type SignalTone = "good" | "watch" | "danger" | "neutral";

export interface Mission {
  id: string;
  isFallback?: boolean;
  title: string;
  reason: string;
  impact: string;
  risk: string;
  owner: DelegationTarget;
  action: CommandAction;
  score: number;
  dueLabel: string;
  tags: string[];
  source: string;
  confidence: string;
  freshness: string;
}

export interface SquadItem {
  id: DelegationTarget;
  label: string;
  role: string;
  status: string;
  tone: SignalTone;
  count: number;
}

export interface SignalItem {
  id: string;
  label: string;
  value: string;
  detail: string;
  tone: SignalTone;
}

export interface DecisionItem {
  id: string;
  title: string;
  body: string;
  target: string;
}

export interface CommandData {
  operationId: string;
  operationTitle: string;
  todayBrief: string;
  nextAction: CommandAction;
  nextActionLabel: string;
  missions: Mission[];
  squad: SquadItem[];
  signals: SignalItem[];
  decisions: DecisionItem[];
}

interface BuildInput {
  tasks: Task[];
  stats: TaskStats;
  syncStatus: SyncStatus;
  settings: Settings | null;
}

const missionKeywords = [
  "FOCUS",
  "focus",
  "架電",
  "返信",
  "LP",
  "送信",
  "アポ",
  "IS",
  "X",
  "音声",
  "FB",
  "フォーム",
  "CVR",
  "Notion",
  "Grok",
  "Codex",
  "Hermes",
];

const ownerLabels: Record<DelegationTarget, string> = {
  taiga: "Taiga",
  codex: providerLabel("codex"),
  claude: providerLabel("claude"),
  grok: "Grok",
  hermes: "Hermes",
};

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

function operationId(): string {
  const d = new Date();
  return `作戦 ${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(
    2,
    "0"
  )}`;
}

function dueDistance(dueDate: string | null): number | null {
  if (!dueDate) return null;
  const today = new Date();
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const due = new Date(`${dueDate}T00:00:00`).getTime();
  return Math.round((due - base) / 86400000);
}

function dueLabel(task: Task): string {
  const distance = dueDistance(task.dueDate);
  if (distance === null) return "期限なし";
  if (distance < 0) return `${Math.abs(distance)}日遅れ`;
  if (distance === 0) return "今日";
  if (distance === 1) return "明日";
  return `${distance}日後`;
}

function containsAny(text: string, words: string[]): boolean {
  return words.some((word) => text.includes(word));
}

function extractTags(task: Task): string[] {
  const text = `${task.title} ${task.description}`;
  const found = missionKeywords.filter((word) => text.includes(word));
  if (task.priority === "high") found.unshift("HIGH");
  if (dueDistance(task.dueDate) === 0) found.unshift("TODAY");
  if ((dueDistance(task.dueDate) ?? 1) < 0) found.unshift("OVERDUE");
  return Array.from(new Set(found)).slice(0, 4);
}

function scoreTask(task: Task): number {
  const text = `${task.title} ${task.description}`;
  const distance = dueDistance(task.dueDate);
  const updatedAgeHours = Math.max(0, (Date.now() - new Date(task.updatedAt).getTime()) / 3600000);

  let score = 0;
  if (task.priority === "high") score += 45;
  if (distance !== null && distance < 0) score += 60;
  if (distance === 0) score += 42;
  if (containsAny(text, ["FOCUS", "focus", "フォーム", "送信", "LP", "CVR"])) score += 28;
  if (containsAny(text, ["架電", "返信", "アポ", "前確"])) score += 34;
  if (containsAny(text, ["IS", "音声", "FB", "Sales AI", "勝ち", "NG"])) score += 32;
  if (containsAny(text, ["X", "Grok", "投稿", "導線", "競合"])) score += 22;
  if (updatedAgeHours < 24) score += 14;
  else if (updatedAgeHours < 72) score += 8;
  return score;
}

function ownerFor(task: Task): DelegationTarget {
  const text = `${task.title} ${task.description}`;
  if (containsAny(text, ["架電", "音声", "FB", "前確", "商談", "電話"])) return "taiga";
  if (containsAny(text, ["実装", "コード", "LP", "CVR", "フォーム", "送信", "Codex"])) return "codex";
  if (containsAny(text, ["X", "Grok", "競合", "市場", "投稿"])) return "grok";
  if (containsAny(text, ["監視", "エラー", "送信状況", "Hermes", "ログ"])) return "hermes";
  return "claude";
}

function actionFor(owner: DelegationTarget, task: Task): CommandAction {
  if (owner === "taiga") return "C";
  if (owner === "hermes" || owner === "grok") return "A";
  if (containsAny(`${task.title} ${task.description}`, ["実装", "修正", "LP"])) return "B";
  return "B";
}

function reasonFor(task: Task): string {
  const distance = dueDistance(task.dueDate);
  if (distance !== null && distance < 0) return "期限を過ぎていて、今日の集中対象から外すと遅延が積み上がる。";
  if (distance === 0) return "今日が期限。朝の時点で着手順を決める価値が高い。";
  if (task.priority === "high") return "高優先タスク。今日の勝ち筋に直結する候補として扱う。";
  return "未完了タスクの中で事業インパクト語と更新鮮度が高い。";
}

function impactFor(task: Task, owner: DelegationTarget): string {
  const text = `${task.title} ${task.description}`;
  if (containsAny(text, ["架電", "返信", "アポ"])) return "アポ獲得と売上化に直結。";
  if (containsAny(text, ["LP", "CVR", "フォーム", "送信"])) return "FOCUS45の獲得導線と事故防止に効く。";
  if (containsAny(text, ["IS", "音声", "FB", "Sales AI"])) return "IS品質を上げ、たいがさんの時間を取り戻す。";
  if (owner === "grok") return "外部変化を早く拾い、発信と導線の鮮度を上げる。";
  return "今日の意思決定コストを下げ、実行速度を上げる。";
}

function riskFor(task: Task): string {
  const text = `${task.title} ${task.description}`;
  if (containsAny(text, ["架電", "返信", "アポ"])) return "温度感のあるリードや商談機会が冷める。";
  if (containsAny(text, ["送信", "フォーム", "LP"])) return "検証サイクルが遅れ、改善判断が翌日にずれる。";
  if (containsAny(text, ["IS", "音声", "FB"])) return "メンバー育成の詰まりが残り、たいがさんの負荷が戻る。";
  return "未完了のまま残り、明日の判断ノイズになる。";
}

function fallbackMissions(): Mission[] {
  return [
    {
      id: "fallback-command",
      isFallback: true,
      title: "今日の勝ち筋を1件だけ決める",
      reason: "司令塔の価値は、朝の最重要を迷わず決めること。",
      impact: "最初の行動が決まり、Todo一覧を見る時間を削れる。",
      risk: "タスクが空のままだと、司令塔が判断材料を持てない。",
      owner: "taiga",
      action: "B",
      score: 1,
      dueLabel: "今すぐ",
      tags: ["LAUNCH"],
      source: "司令塔（ローカル）",
      confidence: "setup",
      freshness: "今",
    },
  ];
}

function buildMissions(tasks: Task[]): Mission[] {
  const ranked = tasks
    .filter((task) => !task.completed && !task.deleted)
    .map((task) => ({ task, score: scoreTask(task) }))
    .sort((a, b) => b.score - a.score || b.task.updatedAt.localeCompare(a.task.updatedAt))
    .slice(0, 3);

  if (!ranked.length) return fallbackMissions();

  return ranked.map(({ task, score }) => {
    const owner = ownerFor(task);
    const updatedAgeHours = Math.max(0, (Date.now() - new Date(task.updatedAt).getTime()) / 3600000);
    return {
      id: task.id,
      title: task.title,
      reason: reasonFor(task),
      impact: impactFor(task, owner),
      risk: riskFor(task),
      owner,
      action: actionFor(owner, task),
      score,
      dueLabel: dueLabel(task),
      tags: extractTags(task),
      source: task.notionPageId ? "Flow + Notion" : "Flow local DB",
      confidence: score >= 80 ? "high" : score >= 45 ? "medium" : "rule v0",
      freshness: updatedAgeHours < 24 ? "24h以内" : updatedAgeHours < 72 ? "3日以内" : "古め",
    };
  });
}

function operationTitle(missions: Mission[]): string {
  if (!missions.length || missions.every((mission) => mission.isFallback)) return "今日の勝ち筋を1件だけ決める";
  const text = missions.map((m) => `${m.title} ${m.tags.join(" ")}`).join(" ");
  if (containsAny(text, ["IS", "音声", "FB", "Sales AI"])) return "IS完成スプリントを止めない";
  if (containsAny(text, ["架電", "返信", "アポ"])) return "温度の高い接点を今日中に前へ進める";
  if (containsAny(text, ["FOCUS", "フォーム", "送信", "LP", "CVR"])) return "FOCUS45の獲得導線を前進させる";
  if (containsAny(text, ["X", "Grok", "投稿"])) return "X導線の鮮度と勝ち筋を作る";
  return "今日の勝ち筋を3件に絞る";
}

function nextAction(missions: Mission[]): { action: CommandAction; label: string } {
  const first = missions[0];
  if (!first) return { action: "B", label: "ミッション案を作る" };
  const labels: Record<CommandAction, string> = {
    A: "状況を確認する",
      B: "案を作る",
      C: "実行準備する",
      D: "一旦保留する",
    };
  return { action: first.action, label: labels[first.action] };
}

function buildSquad(missions: Mission[]): SquadItem[] {
  const base: Array<Omit<SquadItem, "count" | "tone" | "status"> & { activeStatus: string }> = [
    { id: "taiga", label: "Taiga", role: "判断と架電", activeStatus: "実行待ち" },
    { id: "codex", label: providerLabel("codex"), role: "実装と検証", activeStatus: "発注候補あり" },
    { id: "claude", label: providerLabel("claude"), role: "整理と調査", activeStatus: "整理候補あり" },
    { id: "grok", label: "Grok", role: "X/市場の鮮度", activeStatus: "調査候補あり" },
    { id: "hermes", label: "Hermes", role: "監視と定点確認", activeStatus: "監視候補あり" },
  ];

  return base.map((item) => {
    const count = missions.filter((mission) => mission.owner === item.id).length;
    return {
      ...item,
      count,
      tone: count > 0 ? "good" : "neutral",
      status: count > 0 ? item.activeStatus : "待機",
    };
  });
}

function buildSignals(input: BuildInput): SignalItem[] {
  const { stats, syncStatus, settings } = input;
  const hasNotion = Boolean(settings?.hasNotionToken && settings?.notionDataSourceId);
  const syncTone: SignalTone =
    syncStatus.state === "error" ? "danger" : syncStatus.pendingCount > 0 ? "watch" : "good";

  return [
    {
      id: "missions",
      label: "Tasks",
      value: `${stats.active} active`,
      detail: stats.high > 0 && stats.today > 0
        ? `高優先 ${stats.high}件・今日締め ${stats.today}件あり。早めに対処が必要です。`
        : stats.high > 0
        ? `高優先 ${stats.high}件あり。今日中に着手候補。`
        : stats.today > 0
        ? `今日締め ${stats.today}件あり。期限切れに注意。`
        : `${stats.today} today / ${stats.high} high`,
      tone: stats.high > 0 || stats.today > 0 ? "watch" : "neutral",
    },
    {
      id: "notion",
      label: "Notion",
      value: input.syncStatus.state === "error" ? "error" : hasNotion ? "configured" : "unconfigured",
      detail:
        input.syncStatus.state === "error"
          ? `同期エラー: ${input.syncStatus.lastError || input.syncStatus.message || "不明なエラー"}`
          : hasNotion
          ? "設定済み。直近同期が成功した時のみ緑になります。"
          : "Notion 同期先未設定。設定 > Notion タブで接続してください。",
      tone: input.syncStatus.state === "error" ? "danger" : hasNotion ? "watch" : "neutral",
    },
    {
      id: "sync",
      label: "Sync",
      value: syncStatus.state,
      detail:
        syncStatus.state === "error"
          ? syncStatus.lastError || syncStatus.message || "sync failed"
          :
        syncStatus.pendingCount > 0
          ? `${syncStatus.pendingCount} pending`
          : syncStatus.message || "outbox clear",
      tone: syncTone,
    },
    {
      id: "core",
      label: "Command Core",
      value: "rule v0",
      detail: "AI判断は次フェーズ。今日は根拠つきルールで動く",
      tone: "good",
    },
  ];
}

function buildDecisions(missions: Mission[], input: BuildInput): DecisionItem[] {
  const top = missions[0];
  return [
    {
      id: "why-order",
      title: "なぜこの順番か",
      body: top
        ? `先頭ミッションは score ${top.score}。期限・高優先・事業インパクト語・更新鮮度で並べ替えた。`
        : "ミッションがないため、まず判断材料を作る必要がある。",
      target: "current-status.md / Obsidian",
    },
    {
      id: "sources",
      title: "読んだソース",
      body: `Flow tasks, stats, sync status. Today key: ${todayKey()}. Notion: ${
        input.settings?.hasNotionToken ? "configured" : "not configured"
      }.`,
      target: "Decision Log",
    },
    {
      id: "writeback",
      title: "記録先候補",
      body: "仕事の現在地が変わるならcurrent-status.md。判断理由として残すならObsidian。共有情報ならNotion。",
      target: "保存前にA/B/C/D確認",
    },
  ];
}

export function buildCommandData(input: BuildInput): CommandData {
  const missions = buildMissions(input.tasks);
  const next = nextAction(missions);
  const title = operationTitle(missions);
  const top = missions[0];

  return {
    operationId: operationId(),
    operationTitle: title,
    todayBrief: top
      ? `今日の勝ち筋は「${top.title}」を起点に、${ownerLabels[top.owner]}へ最初の指令を切ること。`
      : "今日の勝ち筋をミッションとして1件だけ登録する。",
    nextAction: next.action,
    nextActionLabel: next.label,
    missions,
    squad: buildSquad(missions),
    signals: buildSignals(input),
    decisions: buildDecisions(missions, input),
  };
}
